import {ActivatedRoute, Router} from '@angular/router';

import {Subscription} from 'rxjs';

import {
    ApiError,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    DocumentStatusesEnum,
    ReportsService,
    AuthApiService,
    DocumentReportInterface,
    DocumentReportModel,
    DocumentFilterModel,
} from '@spa/api';

import {Helper} from 'appShared/classes';
import {PanelPaths} from 'appShared/paths';
import {Pagination} from 'appShared/components';
import {UnspecifiedKeysEnum} from 'appShared/enums';
import {CacheCollectionService} from 'appShared/services';
import {ItemsPerPageSelector} from 'appShared/widgets';
import {InitialComponentDataValidationException} from 'appShared/exceptions';

import {EventsService} from '../services';
import {DocumentsReportResolverDataInterface} from '../interfaces';
import {DocumentsReportResolverErrorStatesEnum, EnrollTypesEnum, IsHiddenForClientDocumentEnum} from '../enums';

import {
    CorporateDocumentSelector,
    IndividualDocumentSelector,
} from 'appAssets/descriptors';

import {DocumentsReportMessages} from 'appAssets/messages';

import {ReportComponent} from './report-component';

export abstract class DocumentsReportComponent extends ReportComponent<DocumentReportInterface, DocumentReportModel> {
    private panelPaths: typeof PanelPaths;
    private activatedRouteSubscription: Subscription;

    protected constructor(
        protected authApiService: AuthApiService,
        protected reportsService: ReportsService,
        protected activatedRoute: ActivatedRoute,
        protected router: Router,
        protected cacheCollectionService: CacheCollectionService,
        public eventsService: EventsService,
    ) {
        super(router, eventsService, cacheCollectionService);

        this.filterModel = this.getInitialFilterModel();
        this.panelPaths = PanelPaths;
    }

    onInit(): void {
        super.onInit();

        this.activatedRouteSubscription = this.activatedRoute.data
            .subscribe((result: { data: ServerResponseData<DocumentsReportResolverDataInterface> }) => {
                this.doInitialDataValidationCheck(result.data, () => {
                    this.filterControls = (result.data.data as DocumentsReportResolverDataInterface).filterControls;
                    this.dataTableConfig = (result.data.data as DocumentsReportResolverDataInterface).dataTableConfig;
                    this.modelsCollection = (result.data.data as DocumentsReportResolverDataInterface).modelsCollection;
                    const start = Pagination.calculateStartPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const end = Pagination.calculateEndPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const paginatedCollection = this.paginateCollection<DocumentReportModel>(this.modelsCollection, start, end);


                    this.paginationConfig.totalItems = this.modelsCollection.length;

                    this.dataTableConfig.rows = this.createDataSource(paginatedCollection);
                });
            });
    }

    afterViewInit(): void {
        super.afterViewInit();
    }

    onDestroy(): void {
        super.onDestroy();

        if (this.activatedRouteSubscription) {
            this.activatedRouteSubscription.unsubscribe();
        }

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    getReportHandler(filter: DocumentFilterModel): ServerResponse<DocumentReportInterface[]> {
        return this.reportsService.getDocumentsReport(filter);
    }

    getFilterModel(data: { [p: string]: number | string | boolean }): DocumentFilterModel {
        const itemsPerPage = data[ItemsPerPageSelector] as number;
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk(itemsPerPage);

        const documentStatus = data[DocumentFilterModel.getDocumentStatusesSelector()] as number;
        const enrollType = data[DocumentFilterModel.getEnrollTypeSelector()] as string;
        const isHiddenForClient = data[DocumentFilterModel.getIsHiddenForClientSelector()] as number;

        let isSubDocumentType = false;
        let documentType = data[DocumentFilterModel.getDocumentTypesSelector()] as string;

        let hiddenForClient: boolean = null;

        switch (isHiddenForClient) {
            case IsHiddenForClientDocumentEnum.No:
                hiddenForClient = false;
                break;
            case IsHiddenForClientDocumentEnum.Yes:
                hiddenForClient = true;
        }

        if (documentType !== UnspecifiedKeysEnum.UnspecifiedDocumentKey) {
            const subDocumentTypes = Helper.enumsHelper().getProofOfAddressDocumentTypes()
                .extendTypes(Helper.enumsHelper().getProofOfIdentityDocumentTypes().getTypes());

            isSubDocumentType = (subDocumentTypes.getTypeKeys().indexOf(documentType) >= 0);
        } else {
            documentType = null;
        }

        return new DocumentFilterModel(
            null,
            !isSubDocumentType ? ((documentType !== null) ? [documentType] : null) : null,
            !isSubDocumentType ? null : ((documentType !== null) ? documentType : null),
            (documentStatus !== DocumentStatusesEnum.All) ? [documentStatus] : null,
            null,
            (enrollType !== EnrollTypesEnum.All) ? enrollType : null,
            data[DocumentFilterModel.getEnrollNameSelector()] as string,
            data[DocumentFilterModel.getExpirationDateFromSelector()] as string,
            data[DocumentFilterModel.getExpirationDateToSelector()] as string,
            data[DocumentFilterModel.getCreatedDateSelector()] as string,
            hiddenForClient,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            DocumentReportModel.getUploadedStampSelector(),
            false,
            data[DocumentFilterModel.getDescriptionSelector()] as string,
        );
    }

    mapFilterResults(results: DocumentReportInterface[]): DocumentReportModel[] {
        return results.map((document: DocumentReportInterface) => {
            return new DocumentReportModel(document);
        });
    }

    validateInitialData(
        response: ServerResponseData<DocumentsReportResolverDataInterface>
    ): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const messages = (response.data as ApiError[]).map((error: ApiError) => {
                if (error.status !== DocumentsReportResolverErrorStatesEnum.GetDocumentFailed) {
                    return DocumentsReportMessages.UnknownError.message;
                }

                return DocumentsReportMessages.GetDocumentsFailed.message;
            });

            throw new InitialComponentDataValidationException(messages);
        }
    }

    onRowClicked(rowId: number | string): void {
        const filteredDocuments = this.modelsCollection
            .filter((document: DocumentReportModel) => document.documentId === rowId);

        if (filteredDocuments.length > 0) {
            let clientTypePath = '';

            if (filteredDocuments[0].enrollType !== EnrollTypesEnum.Corporate) {
                clientTypePath = IndividualDocumentSelector;
            } else {
                clientTypePath = CorporateDocumentSelector;
            }

            this.router.navigate([
                this.panelPaths.root,
                this.panelPaths.children.documents.path,
                this.panelPaths.children.documents.children.edit.path,
                rowId,
                clientTypePath
            ]).finally();
        }
    }
}
